import Box from '@/common/components/Box'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { useEquipmentModelIntegrationCodes } from '@/features/equipmentModels/hooks/useEquipmentModelIntegrationCodes'
import IntegrationCodeCard from '@/features/equipmentModels/pages/Show/components/IntegrationsTab/IntegrationCodeCard'
import IntegrationDetails from '@/features/equipmentModels/pages/Show/components/IntegrationsTab/IntegrationDetails'
import { EquipmentModelIntegrationCodeData } from '@/features/equipmentModels/types/equipmentModelIntegrationCode'
import { Search } from 'lucide-react'
import { ChangeEvent, useEffect, useState } from 'react'

interface IntegrationsTabProps {
  equipmentModelId: string
}

const IntegrationsTab = ({ equipmentModelId }: IntegrationsTabProps) => {
  const [integrationsSearch, setIntegrationsSearch] = useDebounce('')
  const [selectedIntegrationCodeId, setSelectedIntegrationCodeId] = useState<string | undefined>()
  const [hasInProgressIntegration, setHasInProgressIntegration] = useState(false)

  const { data: integrationCodes = [], isPending } = useEquipmentModelIntegrationCodes<
    EquipmentModelIntegrationCodeData[]
  >({
    equipmentModelId,
    filters: { search: integrationsSearch },
    refetchInterval: hasInProgressIntegration ? 30000 : false,
  })

  useEffect(() => {
    setHasInProgressIntegration(integrationCodes.some((code) => code.integration_status === 'link_in_progress'))
  }, [integrationCodes])

  useEffect(() => {
    if (selectedIntegrationCodeId) return
    const firstId = integrationCodes[0]?.id
    if (!firstId) return
    setSelectedIntegrationCodeId(firstId)
  }, [integrationCodes, selectedIntegrationCodeId])

  return (
    <div className="grid grid-cols-2 gap-2 items-start">
      <Box className="max-h-[calc(100vh-200px)] overflow-y-auto flex flex-col gap-2">
        <p className="text-sm font-bold">Integrações</p>

        <TextField
          placeholder="Busque por Nome"
          value={integrationsSearch}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setIntegrationsSearch(event.target.value)}
          LeftIcon={<Search />}
          dataTestid="integrations_search_input"
        />

        {isPending ? (
          <p className="italic text-xs">Carregando...</p>
        ) : integrationCodes.length === 0 ? (
          <p className="italic text-xs text-white-400">Nenhuma integração encontrada.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {integrationCodes.map((code) => (
              <IntegrationCodeCard
                key={code.id}
                integrationCode={code}
                isSelected={code.id === selectedIntegrationCodeId}
                onSelect={() => setSelectedIntegrationCodeId(code.id)}
              />
            ))}
          </div>
        )}
      </Box>

      <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
        <IntegrationDetails integrationCodeId={selectedIntegrationCodeId} />
      </div>
    </div>
  )
}

export default IntegrationsTab
